import {
    AsAllowed,
    AsHidden,
    AsRestricted,
    BlockLike,
    LimitationProps,
    Restricted
} from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';
import i18next from 'i18next';
import { calculatePageOrder } from '~/utils/page.utils';
import { getArticlePrefix } from '~/utils/text.utils';
import { ACTION_BLOCKS_WITH_RULES_TO_DESCRIBE } from '~/components/Builder/Common/CreationLimitations/creationLimitations';
import { Statsig } from 'statsig-react';
import { trackError } from '~/services/logger/error.utils';

export interface ForbidMoreThanOneActionBlockInPageRestriction
    extends Restricted {
    issueType: 'forbidMoreThanOneActionBlockInPage';
    solutionData: {
        newPageOrder: number;
        blockType: BlockLike['type'];
        newPageName: string;
    };
}

export const forbidMoreThanOneActionBlockInPage = ({
    targetFlow,
    targetPage,
    sourceBlock: { type: blockTypeToCreate }
}: LimitationProps<'targetPage' | 'targetFlow'>) => {
    const t = i18next.getFixedT(null, 'builder', 'creation_limitation_dialog');

    if (!targetPage) {
        return AsAllowed;
    }

    // only allow multiple service selections on page, any other action block should be the only action on page
    const isActionBlockCreateAllowedOnSamePage =
        !targetPage.actionBlock ||
        (targetPage.actionBlock.type === 'service_selection_block' &&
            blockTypeToCreate === 'service_selection_block');

    if (isActionBlockCreateAllowedOnSamePage) {
        return AsAllowed;
    }

    const currBlockName =
        ACTION_BLOCKS_WITH_RULES_TO_DESCRIBE[targetPage.actionBlock.type]?.name;
    const shouldAddBeforePage =
        (blockTypeToCreate === 'service_selection_block' &&
            targetPage.isInvoicePage) ||
        (blockTypeToCreate === 'service_selection_block' &&
            targetPage.contractBlock?.hasServicesRecaps) ||
        (blockTypeToCreate === 'schedule_container_block' &&
            targetPage.contractBlock?.contractHTMLBlock.hasSmartFieldsWithSourceType(
                'session'
            ));

    const newPageOrder = calculatePageOrder({
        pages: targetFlow.pages,
        relativeToPage: targetPage,
        shouldAddBeforePage
    });

    return AsRestricted<ForbidMoreThanOneActionBlockInPageRestriction>({
        issueType: 'forbidMoreThanOneActionBlockInPage',
        reason: {
            short: t('same_page_limitation.title'),
            long: t('same_page_limitation.description', {
                currBlockName: `${getArticlePrefix(
                    currBlockName
                )} ${currBlockName}`
            }),
            cta: t('default_approve_cta')
        },
        solutionData: {
            newPageOrder: newPageOrder,
            blockType: blockTypeToCreate,
            newPageName:
                ACTION_BLOCKS_WITH_RULES_TO_DESCRIBE[blockTypeToCreate]
                    ?.newPageName
        }
    });
};

export interface ChargeBlockCanOnlyBeOnLastPageInPublicFileRestriction
    extends Restricted {
    issueType: 'chargeBlockCanOnlyBeOnLastPageInPublicFile';
    solutionData: {
        droppedItemId: string;
        dropTargetId: string;
        direction: 'top' | 'bottom';
    };
}

export const chargeBlockCanOnlyBeOnLastPageInPublicFile = ({
    targetFlow,
    targetPage
}: LimitationProps<'targetFlow' | 'targetPage'>) => {
    if (!targetFlow.isLeadForm || !targetFlow.hasChargeBlock) {
        return AsAllowed;
    }

    const t = i18next.getFixedT(
        null,
        'builder',
        'creation_limitation_dialog.lead_form_charge_limitation'
    );

    if (!targetPage || targetPage.order >= targetFlow.pageWithPay?.order!) {
        return AsRestricted<ChargeBlockCanOnlyBeOnLastPageInPublicFileRestriction>(
            {
                issueType: 'chargeBlockCanOnlyBeOnLastPageInPublicFile',
                solutionData: {
                    direction: 'top',
                    droppedItemId: targetPage?.page_id,
                    dropTargetId: targetFlow.pageWithPay?.page_id!
                },
                reason: {
                    short: t('title'),
                    long: t('description'),
                    cta: t('approve_cta'),
                    rejectCta: t('reject_cta')
                }
            }
        );
    }

    return AsAllowed;
};

export const forbidOnGateDisabled = (gateName: string) => {
    try {
        if (!Statsig.checkGate(gateName)) {
            return AsHidden(`${gateName} gate disabled`);
        }
        return AsAllowed;
    } catch (error) {
        trackError({
            context: {
                builder_limitations: true,
                statsig: true
            },
            error,
            message: 'builder limitations: failed to check gate'
        });
        return AsHidden(`failed checking gate`);
    }
};
